import React, { useEffect, useMemo, useState } from "react";
import { logoutSpotify } from "./spotifyAuth";
import { spotifyApi } from "./spotifyApi";
import { setPlayerState } from "../state/playerState";
import { initSpotifyPlayer, playUri, playContext, playContextOnly, pausePlayback, resumePlayback, nextTrack, previousTrack, activateSpotifyPlayer, setPlayerVolume, seekToPosition } from "./spotifyPlayer";


function buildAmbientBackground(track) {
  const img =
    track?.album?.images?.[0]?.url ||
    track?.album?.images?.[1]?.url ||
    "";

  if (!img) {
    return {
      background:
        "radial-gradient(circle at top, rgba(40,40,50,0.9), rgba(0,0,0,0.98) 70%)",
    };
  }

  return {
    backgroundImage: `
      linear-gradient(
        to bottom,
        rgba(0,0,0,0.28),
        rgba(0,0,0,0.82)
      ),
      url(${img})
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };
}

function normalizeSpotifyPlaybackTrack(item, fallbackDuration) {
  if (!item) return null;

  return {
    id: item.id,
    name: item.name,
    uri: item.uri,
    duration_ms: item.duration_ms || fallbackDuration || 0,
    artists: item.artists || [],
    album: {
      name: item.album?.name,
      images: item.album?.images || [],
    },
  };
}

export default function SpotifyPage() {
  const [serverTokenReady, setServerTokenReady] = useState(null);
  const [status, setStatus] = useState("Checking Spotify connection...");
  const [profile, setProfile] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [topTracks, setTopTracks] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [likedSongs, setLikedSongs] = useState([]);
  const [savedAlbums, setSavedAlbums] = useState([]);
  const [followedArtists, setFollowedArtists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedPlaylistTracks, setSelectedPlaylistTracks] = useState([]);
  const [libraryFilter, setLibraryFilter] = useState("playlists");
  const [librarySort, setLibrarySort] = useState("recents");
  const [selectedLibraryPage, setSelectedLibraryPage] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [activeTrack, setActiveTrack] = useState(null);
  const [isPaused, setIsPaused] = useState(true);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [showHiFiPlayer, setShowHiFiPlayer] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistTracks, setArtistTracks] = useState([]);
  const [artistAlbums, setArtistAlbums] = useState([]);
  const [artistSingles, setArtistSingles] = useState([]);
  const [artistAppearsOn, setArtistAppearsOn] = useState([]);
  const [artistCompilations, setArtistCompilations] = useState([]);
  const [relatedArtists, setRelatedArtists] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);


  const ambientStyle = useMemo(
    () => buildAmbientBackground(activeTrack),
    [activeTrack]
  );

  useEffect(() => {
    let alive = true;

    async function checkServerSpotifyToken() {
      try {
        const res = await fetch("/api/spotify/token");
        const data = await res.json().catch(() => ({}));

        if (!alive) return;

        if (res.ok && data.access_token) {
          setServerTokenReady(true);
          setStatus("Spotify connected.");
        } else {
          setServerTokenReady(false);
          setStatus(data?.error || "Spotify setup required.");
        }
      } catch (err) {
        if (!alive) return;
        setServerTokenReady(false);
        setStatus("Spotify setup required. Server token check failed.");
      }
    }

    checkServerSpotifyToken();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (serverTokenReady !== true) return;

    let alive = true;

    initSpotifyPlayer(
      (id) => {
        if (!alive) return;
        setDeviceId(id);
      },
      (state) => {
        if (!alive) return;
        if (!state) return;

        setIsPaused(state.paused);
        setPositionMs(state.position || 0);
        setDurationMs(state.duration || 0);

        const current = state.track_window?.current_track;
        if (current) {
          setActiveTrack({
            name: current.name,
            uri: current.uri,
            duration_ms: state.duration,
            artists: current.artists || [],
            album: {
              name: current.album?.name,
              images: current.album?.images || [],
            },
          });
        }
      }
    ).catch((err) => {
      console.error("Spotify player init failed:", err);
      if (alive) setStatus("Spotify player unavailable. Check Spotify setup.");
    });

    return () => {
      alive = false;
    };
  }, [serverTokenReady]);

  useEffect(() => {
    if (serverTokenReady !== true) return;

    let alive = true;

    async function syncPlaybackState() {
      try {
        const state = await spotifyApi.playbackState();

        if (!alive || !state) return;

        setIsPaused(!state.is_playing);
        setPositionMs(state.progress_ms || 0);
        setDurationMs(state.item?.duration_ms || 0);

        const normalized = normalizeSpotifyPlaybackTrack(state.item, state.item?.duration_ms);
        if (normalized) setActiveTrack(normalized);
      } catch (err) {
        console.warn("Spotify playback sync failed:", err.message);
      }
    }

    syncPlaybackState();
    const interval = setInterval(syncPlaybackState, 2500);

    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [serverTokenReady]);


  useEffect(() => {
    if (!activeTrack) return;

    const art =
      activeTrack.album?.images?.[0]?.url ||
      activeTrack.album?.images?.[1]?.url ||
      "";

    setPlayerState({
      source: "spotify",
      playing: !isPaused,
      title: activeTrack.name || "",
      artist: activeTrack.artists?.map((a) => a.name).join(", ") || "",
      album: activeTrack.album?.name || "",
      artwork: art,
      duration: Math.floor((durationMs || activeTrack.duration_ms || 0) / 1000),
      position: Math.floor((positionMs || 0) / 1000),
      quality: "Spotify Premium",
      codec: "OGG",
      bitDepth: null,
      sampleRate: 44100,
      bitrate: 320,
    });
  }, [activeTrack, isPaused, positionMs, durationMs]);

  async function openPlaylist(playlist) {
    if (!playlist?.id) return;

    try {
      setSelectedPlaylistTracks([]);

      const data = await spotifyApi.playlist(playlist.id);

      setSelectedLibraryPage(null);
      setSelectedPlaylist(data);
      setSelectedPlaylistTracks(data.tracks?.items || []);

      try {
        const page = await spotifyApi.playlistTracks(playlist.id);
        setSelectedPlaylistTracks(page.items || data.tracks?.items || []);
      } catch (trackErr) {
        console.warn("Playlist tracks blocked; using playlist context fallback:", trackErr.message);
      }
      setSelectedArtist(null);
      setSelectedAlbum(null);
    } catch (err) {
      console.error("Playlist load failed:", err);
    }
  }

  async function openArtist(artist) {
    if (!artist?.id) return;

    setSelectedLibraryPage(null);
    setSelectedArtist(artist);
    setSelectedAlbum(null);
    setSearchResults(null);

    setArtistTracks([]);
    setArtistAlbums([]);
    setArtistSingles([]);
    setArtistAppearsOn([]);
    setArtistCompilations([]);
    setRelatedArtists([]);

    try {
      const [searchRes, albumsRes, singlesRes, appearsRes, compsRes] = await Promise.allSettled([
        spotifyApi.search(artist.name),
        spotifyApi.artistAlbums(artist.id, "album", 10),
        spotifyApi.artistAlbums(artist.id, "single", 10),
        spotifyApi.artistAlbums(artist.id, "appears_on", 10),
        spotifyApi.artistAlbums(artist.id, "compilation", 10),
      ]);

      if (searchRes.status === "fulfilled") {
        const tracks = searchRes.value?.tracks?.items || [];
        const artists = searchRes.value?.artists?.items || [];

        setArtistTracks(
          tracks.filter((t) =>
            t.artists?.some((a) => a.name.toLowerCase() === artist.name.toLowerCase())
          )
        );

        setRelatedArtists(
          artists.filter((a) => a.id !== artist.id)
        );
      }

      if (albumsRes.status === "fulfilled") setArtistAlbums(albumsRes.value?.items || []);
      if (singlesRes.status === "fulfilled") setArtistSingles(singlesRes.value?.items || []);
      if (appearsRes.status === "fulfilled") setArtistAppearsOn(appearsRes.value?.items || []);
      if (compsRes.status === "fulfilled") setArtistCompilations(compsRes.value?.items || []);
    } catch (err) {
      console.error("Open artist failed:", err);
    }
  }

  async function openAlbum(album) {
    if (!album?.id) return;

    try {
      const data = await spotifyApi.album(album.id);
      setSelectedLibraryPage(null);
      setSelectedAlbum(data);
    } catch (err) {
      console.error("Open album failed:", err);
    }
  }

  async function handleSpotifySearch(e) {
    e.preventDefault();

    const q = searchQuery.trim();

    if (!q) {
      setSearchResults(null);
      return;
    }

    try {
      const results = await spotifyApi.search(q);
      setSearchResults(results);
    } catch (err) {
      console.error("Spotify search failed:", err);
    }
  }


  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setPositionMs((p) => {
        if (p >= durationMs) return p;
        return p + 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isPaused, durationMs]);

  async function refreshPlaybackSoon() {
    setTimeout(async () => {
      try {
        const state = await spotifyApi.playbackState();
        if (!state) return;

        setIsPaused(!state.is_playing);
        setPositionMs(state.progress_ms || 0);
        setDurationMs(state.item?.duration_ms || 0);

        const normalized = normalizeSpotifyPlaybackTrack(state.item, state.item?.duration_ms);
        if (normalized) setActiveTrack(normalized);
      } catch (err) {
        console.warn("Spotify refresh failed:", err.message);
      }
    }, 600);
  }

  async function handleTogglePlay() {
    try {
      if (isPaused) {
        await resumePlayback(deviceId);
      } else {
        await pausePlayback();
      }

      refreshPlaybackSoon();
    } catch (err) {
      console.error("Spotify play/pause failed:", err);
    }
  }

  async function handleNextTrack() {
    try {
      await nextTrack();
      refreshPlaybackSoon();
    } catch (err) {
      console.error("Spotify next failed:", err);
    }
  }

  async function handlePreviousTrack() {
    try {
      await previousTrack();
      refreshPlaybackSoon();
    } catch (err) {
      console.error("Spotify previous failed:", err);
    }
  }


  async function handleVolumeChange(e) {
    const v = Number(e.target.value);
    setVolume(v);
    setPlayerVolume(v);
  }

  async function handleSeek(e) {
    const pct = Number(e.target.value);
    const nextPos = Math.floor((pct / 100) * durationMs);

    setPositionMs(nextPos);
    seekToPosition(nextPos);
  }

  async function handlePlayTrack(track) {
    if (!track?.uri) return;

    if (!deviceId) {
      console.warn("Spotify device not ready yet.");
      return;
    }

    try {
      await activateSpotifyPlayer();

      if (track.context_uri) {
        await playContext(track.context_uri, track.uri, deviceId);
      } else {
        await playUri(track.uri, deviceId);
      }
      setActiveTrack(track);
    } catch (err) {
      console.error("Spotify play failed:", err);
    }
  }

  useEffect(() => {
    if (serverTokenReady !== true) return;

    Promise.allSettled([
      spotifyApi.me(),
      spotifyApi.playlists(),
      spotifyApi.topArtists(),
      spotifyApi.topTracks(),
      spotifyApi.recentlyPlayed(),
      spotifyApi.likedSongs(),
      spotifyApi.savedAlbums(),
      spotifyApi.followedArtists(),
    ]).then(([me, pls, artists, tracks, recent, liked, albums, followed]) => {
      if (me.status === "fulfilled") setProfile(me.value || {});

      if (pls.status === "fulfilled") {
        setPlaylists(pls.value?.items || []);
      } else {
        console.warn("Spotify playlists load failed:", pls.reason?.message || pls.reason);
      }

      if (artists.status === "fulfilled") {
        setTopArtists(artists.value?.items || []);
      }

      if (tracks.status === "fulfilled") {
        setTopTracks(tracks.value?.items || []);
      }

      if (recent.status === "fulfilled") {
        setRecentlyPlayed(recent.value?.items || []);
      }

      if (liked.status === "fulfilled") {
        setLikedSongs(
          (liked.value?.items || [])
            .map((x) => x.track || x)
            .filter(Boolean)
        );
      }

      if (albums.status === "fulfilled") {
        setSavedAlbums(
          (albums.value?.items || [])
            .map((x) => x.album || x)
            .filter(Boolean)
        );
      }

      if (followed.status === "fulfilled") {
        setFollowedArtists(followed.value?.artists?.items || []);
      }
    });
  }, [serverTokenReady]);

  if (serverTokenReady === false && !profile) {
    return (
      <div style={styles.loginPage}>
        <div style={styles.loginPanel}>
          <div style={styles.eyebrow}>Poster Junkie Music</div>
          <h1 style={styles.title}>Spotify setup required</h1>
          <p style={styles.status}>{status}</p>
          <p style={styles.status}>
            Add the server Spotify refresh token configuration, then reload Napflix.
          </p>
        </div>
      </div>
    );
  }

  function sortLibraryItems(items, type) {
    const copy = [...(items || [])];

    if (librarySort === "alpha") {
      return copy.sort((a, b) => {
        const aa = type === "albums" ? (a.album || a).name : a.name;
        const bb = type === "albums" ? (b.album || b).name : b.name;
        return String(aa || "").localeCompare(String(bb || ""));
      });
    }

    if (librarySort === "recently_added") {
      return copy.reverse();
    }

    return copy;
  }

  const visiblePlaylists = sortLibraryItems(playlists, "playlists");
  const visibleArtists = sortLibraryItems(followedArtists, "artists");
  const visibleAlbums = sortLibraryItems(savedAlbums, "albums");

  

const recentTracks = recentlyPlayed.map((x) => x.track).filter(Boolean);

  return (
    <div style={styles.shell}>
      <aside style={styles.sidebar}>
        <div style={styles.sideTitle}>Your Library</div>
        <button
          style={libraryFilter === "playlists" ? styles.sideButtonOn : styles.sideButton}
          onClick={() => setLibraryFilter("playlists")}
        >
          Playlists
        </button>
        <button
          style={libraryFilter === "artists" ? styles.sideButtonOn : styles.sideButton}
          onClick={() => setLibraryFilter("artists")}
        >
          Artists
        </button>
        <button
          style={libraryFilter === "albums" ? styles.sideButtonOn : styles.sideButton}
          onClick={() => setLibraryFilter("albums")}
        >
          Albums
        </button>

        <div
          style={styles.likedSongsCard}
          onClick={() => {
            setSelectedLibraryPage("liked");
            setSelectedPlaylist(null);
            setSelectedArtist(null);
            setSelectedAlbum(null);
            setSearchResults(null);
          }}
        >
          <div style={styles.likedIcon}>♥</div>
          <div>
            <div style={styles.sideName}>Liked Songs</div>
            <div style={styles.sideSub}>{likedSongs.length} saved tracks</div>
          </div>
        </div>

        <div style={styles.sortRow}>
          <span style={styles.sortLabel}>Sort</span>
          <select
            value={librarySort}
            onChange={(e) => setLibrarySort(e.target.value)}
            style={styles.sortSelect}
          >
            <option value="recents">Recents</option>
            <option value="recently_added">Recently Added</option>
            <option value="alpha">Alphabetical</option>
          </select>
        </div>

        <div style={styles.sideList}>
          {libraryFilter === "playlists" &&
            visiblePlaylists.map((p) => (
              <div
                key={p.id}
                style={styles.sideItem}
                onClick={() => { console.log("SIDEBAR PLAYLIST CLICK", p.name, p.id); openPlaylist(p); }}
              >
                <img src={p.images?.[0]?.url} alt="" style={styles.sideImg} />
                <div>
                  <div style={styles.sideName}>{p.name}</div>
                  <div style={styles.sideSub}>Playlist</div>
                </div>
              </div>
            ))}

          {libraryFilter === "artists" &&
            visibleArtists.map((artist) => (
              <div
                key={artist.id}
                style={styles.sideItem}
                onClick={() => {
                  console.log("SIDEBAR ARTIST CLICK", artist.name, artist.id);
                  setSelectedPlaylist(null);
                  setSelectedPlaylistTracks([]);
                  setSelectedAlbum(null);
                  setSelectedLibraryPage(null);
                  setSearchResults(null);
                  openArtist(artist);
                }}
              >
                <img src={artist.images?.[0]?.url} alt="" style={styles.sideImg} />
                <div>
                  <div style={styles.sideName}>{artist.name}</div>
                  <div style={styles.sideSub}>Artist</div>
                </div>
              </div>
            ))}

          {libraryFilter === "albums" &&
            visibleAlbums.map((x) => {
              const album = x.album || x;
              return (
                <div
                  key={album.id}
                  style={styles.sideItem}
                  onClick={() => {
                    console.log("SIDEBAR ALBUM CLICK", album.name, album.id);
                    setSelectedPlaylist(null);
                    setSelectedPlaylistTracks([]);
                    setSelectedArtist(null);
                    setSelectedLibraryPage(null);
                    setSearchResults(null);
                    openAlbum(album);
                  }}
                >
                  <img src={album.images?.[0]?.url} alt="" style={styles.sideImg} />
                  <div>
                    <div style={styles.sideName}>{album.name}</div>
                    <div style={styles.sideSub}>
                      {album.artists?.map((a) => a.name).join(", ") || "Album"}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </aside>

      <main style={styles.main}>
        {serverTokenReady === null && (
          <div style={{
            marginBottom: 18,
            padding: "12px 16px",
            borderRadius: 12,
            background: "rgba(255,180,80,.12)",
            border: "1px solid rgba(255,180,80,.22)",
            color: "rgba(255,255,255,.78)",
            fontSize: 13
          }}>
            Checking Spotify server setup...
          </div>
        )}

        <div style={styles.topBar}>
          <div>
            <div style={styles.eyebrow}>Spotify connected</div>
            <h1 style={styles.mainTitle}>
              {profile?.display_name ? `Welcome, ${profile.display_name}` : "Spotify"}
            </h1>
          </div>

          <button
            style={styles.disconnect}
            onClick={() => {
              logoutSpotify();
              setStatus("Local Spotify session cleared. Server Spotify remains configured.");
            }}
          >
            Disconnect
          </button>
        </div>


        <form style={styles.searchWrap} onSubmit={handleSpotifySearch}>
          <input
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Spotify artists, albums, tracks, playlists..."
          />
          <button style={styles.searchButton} type="submit">
            Search
          </button>
        </form>

  
      {selectedLibraryPage === "liked" ? (
        <LikedSongsDetail
          likedSongs={likedSongs}
          onBack={() => setSelectedLibraryPage(null)}
          onPlayTrack={handlePlayTrack}
        />
      ) : selectedPlaylist ? (
        <PlaylistDetail
          playlist={selectedPlaylist}
          playlistTracks={selectedPlaylistTracks}
          deviceId={deviceId}
          onBack={() => {
            setSelectedPlaylist(null);
            setSelectedPlaylistTracks([]);
          }}
          onPlayTrack={handlePlayTrack}
        />
      ) : selectedAlbum ? (
          <AlbumDetail
            album={selectedAlbum}
            onBack={() => setSelectedAlbum(null)}
            onPlayTrack={handlePlayTrack}
          />
        ) : selectedArtist ? (
          <ArtistDetail
            artist={selectedArtist}
            tracks={artistTracks}
            albums={artistAlbums}
            singles={artistSingles}
            appearsOn={artistAppearsOn}
            compilations={artistCompilations}
            related={relatedArtists}
            onBack={() => {
              setSelectedArtist(null);
              setSelectedAlbum(null);
              setArtistTracks([]);
              setArtistAlbums([]);
              setArtistSingles([]);
              setArtistAppearsOn([]);
              setArtistCompilations([]);
              setRelatedArtists([]);
            }}
            onPlayTrack={handlePlayTrack}
            onOpenArtist={openArtist}
            onOpenAlbum={openAlbum}
          />
        ) : searchResults ? (
          <>
            <Section title="Artists" items={searchResults.artists?.items || []} type="artist" onOpenArtist={openArtist} />
            <Section title="Albums" items={searchResults.albums?.items || []} type="album" onOpenAlbum={openAlbum} />
            <Section title="Tracks" items={searchResults.tracks?.items || []} type="track" onPlayTrack={handlePlayTrack} />
            <Section title="Playlists" items={(searchResults.playlists?.items || []).filter(Boolean)} type="playlist" onOpenPlaylist={openPlaylist} />
          </>
        ) : (
          <>
            <QuickShelf
              playlists={playlists}
              albums={savedAlbums}
              likedSongs={likedSongs}
              onLiked={() => setSelectedLibraryPage("liked")}
              onPlaylist={openPlaylist}
              onAlbum={openAlbum}
            />

            <Section title="Recently Played" items={recentTracks.slice(0, 18)} type="track" onPlayTrack={handlePlayTrack} />
            <Section title="Liked Songs" items={likedSongs.filter(Boolean).slice(0, 18)} type="track" onPlayTrack={handlePlayTrack} />
            <Section title="Your Playlists" items={playlists.slice(0, 24)} type="playlist" onOpenPlaylist={openPlaylist} />
            <Section title="Saved Albums" items={savedAlbums.filter(Boolean).slice(0, 24)} type="album" onOpenAlbum={openAlbum} />
            <Section title="Followed Artists" items={followedArtists.slice(0, 24)} type="artist" onOpenArtist={openArtist} />
            <Section title="Top Tracks" items={topTracks.slice(0, 18)} type="track" onPlayTrack={handlePlayTrack} />
            <Section title="Top Artists" items={topArtists.slice(0, 24)} type="artist" onOpenArtist={openArtist} />
          </>
        )}
      </main>

      <NowPlayingPanel track={activeTrack || recentTracks[0]} />
      <BottomPlayer
        track={activeTrack || recentTracks[0]}
        isPaused={isPaused}
        positionMs={positionMs}
        durationMs={durationMs}
        onTogglePlay={handleTogglePlay}
        onNext={handleNextTrack}
        onPrevious={handlePreviousTrack}
        volume={volume}
        onVolumeChange={handleVolumeChange}
        onSeek={handleSeek}
        onExpand={() => setShowHiFiPlayer(true)}
      />

      {showHiFiPlayer && (
        <HiFiPlayer
          track={activeTrack || recentTracks[0]}
          isPaused={isPaused}
          positionMs={positionMs}
          durationMs={durationMs}
          volume={volume}
          ambientStyle={ambientStyle}
          onClose={() => setShowHiFiPlayer(false)}
          onTogglePlay={handleTogglePlay}
          onNext={handleNextTrack}
          onPrevious={handlePreviousTrack}
          onVolumeChange={handleVolumeChange}
          onSeek={handleSeek}
        />
      )}
    </div>
  );
}

function formatTime(ms = 0) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function NowPlayingPanel({ track }) {
  const image = track?.album?.images?.[0]?.url || track?.album?.images?.[1]?.url;

  return (
    <aside style={styles.nowPlaying}>
      <div style={styles.panelTitle}>Now Playing</div>

      {track ? (
        <>
          {image ? (
            <img src={image} alt="" style={styles.nowImg} />
          ) : (
            <div style={styles.nowImgFallback}>♪</div>
          )}

          <div style={styles.nowTitle}>{track.name}</div>
          <div style={styles.nowSub}>
            {track.artists?.map((a) => a.name).join(", ")}
          </div>
        </>
      ) : (
        <div style={styles.empty}>Start playing something in Spotify.</div>
      )}
    </aside>
  );
}

function BottomPlayer({ track, isPaused, positionMs, durationMs, onTogglePlay, onNext, onPrevious, volume, onVolumeChange, onSeek, onExpand }) {
  const image = track?.album?.images?.[2]?.url || track?.album?.images?.[0]?.url;
  const pct = durationMs ? Math.min(100, (positionMs / durationMs) * 100) : 0;

  return (
    <div style={styles.bottomPlayer}>
      <div style={styles.bottomLeft}>
        {image ? (
          <img
            src={image}
            alt=""
            style={styles.bottomImg}
            onClick={onExpand}
          />
        ) : (
          <div style={styles.bottomImgFallback}>♪</div>
        )}
        <div>
          <div style={styles.bottomTitle}>{track?.name || "Nothing playing"}</div>
          <div style={styles.bottomSub}>{track?.artists?.map((a) => a.name).join(", ") || ""}</div>
        </div>
      </div>

      <div style={styles.bottomCenter}>
        <div style={styles.controls}>
          <button style={styles.controlButton} onClick={onPrevious}>⏮</button>
          <button style={styles.playCircle} onClick={onTogglePlay}>{isPaused ? "▶" : "❚❚"}</button>
          <button style={styles.controlButton} onClick={onNext}>⏭</button>
        </div>

        <div style={styles.progressRow}>
          <span>{formatTime(positionMs)}</span>

          <div style={styles.progressWrap}>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${pct}%` }} />
            </div>

            <input
              type="range"
              min="0"
              max="100"
              value={pct}
              onChange={onSeek}
              style={styles.progressSlider}
            />
          </div>

          <span>{formatTime(durationMs)}</span>
        </div>
      </div>

      <div style={styles.bottomRight}>
        <div style={styles.volumeWrap}>
          <span style={styles.volumeIcon}>🔊</span>

          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={onVolumeChange}
            style={styles.volumeSlider}
          />
        </div>
      </div>
    </div>
  );
}


function HiFiPlayer({
  track,
  isPaused,
  positionMs,
  durationMs,
  volume,
  onClose,
  onTogglePlay,
  onNext,
  onPrevious,
  onVolumeChange,
  onSeek,
}) {
  const image =
    track?.album?.images?.[0]?.url ||
    track?.album?.images?.[1]?.url;

  const pct = durationMs
    ? Math.min(100, (positionMs / durationMs) * 100)
    : 0;

  return (
    <div style={styles.deviceOverlay}>
      <button style={styles.deviceClose} onClick={onClose}>✕</button>

      <div style={styles.deviceScreen}>
        <div style={styles.deviceTopBar}>
          <div style={styles.deviceBrand}>POSTER JUNKIE · SPOTIFY</div>
          <div style={styles.deviceBadges}>
            <span>PCM</span>
            <span>44.1 kHz</span>
            <span>2 ch</span>
            <span>16 bit</span>
          </div>
        </div>

        <div style={styles.deviceMain}>
          <div style={styles.deviceArtWrap}>
            {image ? (
              <img src={image} alt="" style={styles.deviceArtwork} />
            ) : (
              <div style={styles.deviceArtworkFallback}>♪</div>
            )}
          </div>

          <div style={styles.deviceInfo}>
            <div style={styles.deviceTrack}>{track?.name || "Nothing Playing"}</div>
            <div style={styles.deviceArtist}>
              {track?.artists?.map((a) => a.name).join(", ") || "Poster Junkie Player"}
            </div>
            <div style={styles.deviceAlbum}>{track?.album?.name || ""}</div>

            <div style={styles.deviceFormat}>
              <span>WAV</span>
              <span>44.1 kHz</span>
              <span>2 ch</span>
              <span>1.41 Mbps</span>
              <span>16 bit</span>
            </div>

            <div style={styles.devicePcm}>◉ PCM&nbsp;&nbsp;44.1 kHz</div>

            <div style={styles.deviceControls}>
              <button style={styles.deviceControlButton} onClick={onPrevious}>⏮</button>
              <button style={styles.devicePlay} onClick={onTogglePlay}>
                {isPaused ? "▶" : "❚❚"}
              </button>
              <button style={styles.deviceControlButton} onClick={onNext}>⏭</button>
            </div>

            <div style={styles.deviceProgressRow}>
              <span>{formatTime(positionMs)}</span>
              <div style={styles.deviceProgressWrap}>
                <div style={styles.deviceProgressTrack}>
                  <div style={{ ...styles.deviceProgressFill, width: `${pct}%` }} />
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={pct}
                  onChange={onSeek}
                  style={styles.deviceProgressSlider}
                />
              </div>
              <span>{formatTime(durationMs)}</span>
            </div>
          </div>

          <div style={styles.deviceSide}>
            <div style={styles.deviceVolumeLabel}>VOLUME</div>
            <div style={styles.deviceDb}>{Math.round((volume - 1) * 60)}.0 <span>dB</span></div>

            <div style={styles.deviceKnob}>
              <div style={styles.deviceKnobDot} />
            </div>

            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={onVolumeChange}
              style={styles.deviceVolumeSlider}
            />

            <div style={styles.deviceMeters}>
              {Array.from({ length: 22 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.deviceMeter,
                    opacity: i < Math.round(volume * 22) ? 1 : 0.22,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div style={styles.deviceNav}>
          <div style={styles.deviceNavActive}>▥<span>NOW PLAYING</span></div>
          <div>☰<span>QUEUE</span></div>
          <div>♬<span>PLAYLISTS</span></div>
          <div>◎<span>ALBUMS</span></div>
          <div>⌕<span>SEARCH</span></div>
          <div>⚙<span>SETTINGS</span></div>
        </div>
      </div>
    </div>
  );
}

function ArtistDetail({ artist, tracks, albums, singles, appearsOn, compilations, related, onBack, onPlayTrack, onOpenArtist, onOpenAlbum }) {
  const heroImg = artist.images?.[0]?.url;

  return (
    <div>
      <button style={styles.backButton} onClick={onBack}>← Back to Spotify</button>

      <div style={styles.artistHero}>
        {heroImg && <img src={heroImg} alt="" style={styles.artistHeroImg} />}
        <div>
          <div style={styles.eyebrow}>Artist</div>
          <h1 style={styles.artistName}>{artist.name}</h1>
          <div style={styles.artistMeta}>
            {artist.followers?.total ? `${artist.followers.total.toLocaleString()} followers` : ""}
            {artist.genres?.length ? ` · ${artist.genres.slice(0, 5).join(", ")}` : ""}
          </div>
        </div>
      </div>

      <TrackList title="Popular" tracks={tracks} onPlayTrack={onPlayTrack} />
      <Section title="Albums" items={albums} type="album" onOpenAlbum={onOpenAlbum} />
      <Section title="Singles and EPs" items={singles} type="album" onOpenAlbum={onOpenAlbum} />
      <Section title="Appears On" items={appearsOn} type="album" onOpenAlbum={onOpenAlbum} />
      <Section title="Compilations" items={compilations} type="album" onOpenAlbum={onOpenAlbum} />
      <Section title="Fans Also Like" items={related} type="artist" onOpenArtist={onOpenArtist} />
    </div>
  );
}

function AlbumDetail({ album, onBack, onPlayTrack }) {
  const image = album.images?.[0]?.url;
  const tracks = album.tracks?.items || [];

  return (
    <div>
      <button style={styles.backButton} onClick={onBack}>← Back to Artist</button>

      <div style={styles.albumHero}>
        {image && <img src={image} alt="" style={styles.albumHeroImg} />}
        <div>
          <div style={styles.eyebrow}>{album.album_type || "Album"}</div>
          <h1 style={styles.albumName}>{album.name}</h1>
          <div style={styles.artistMeta}>
            {album.artists?.map((a) => a.name).join(", ")}
            {album.release_date ? ` · ${album.release_date.slice(0, 4)}` : ""}
            {album.total_tracks ? ` · ${album.total_tracks} tracks` : ""}
          </div>
        </div>
      </div>

      <TrackList
        title="Tracks"
        tracks={tracks.map((t) => ({ ...t, album, context_uri: album.uri }))}
        onPlayTrack={onPlayTrack}
      />
    </div>
  );
}

function TrackList({ title, tracks, onPlayTrack }) {
  if (!tracks?.length) return null;

  return (
    <section style={styles.section}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <div style={styles.trackList}>
        {tracks.map((track, index) => {
          const image = track.album?.images?.[2]?.url || track.album?.images?.[0]?.url;
          const duration = track.duration_ms
            ? `${Math.floor(track.duration_ms / 60000)}:${String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, "0")}`
            : "";

          return (
            <div key={`${track.id || track.uri}-${index}`} style={styles.trackRow} onClick={() => onPlayTrack?.(track)}>
              <div style={styles.trackNum}>{index + 1}</div>
              {image ? <img src={image} alt="" style={styles.trackImg} /> : <div style={styles.trackImgFallback}>♪</div>}
              <div style={styles.trackInfo}>
                <div style={styles.trackTitle}>{track.name}</div>
                <div style={styles.trackArtist}>{track.artists?.map((a) => a.name).join(", ")}</div>
              </div>
              <div style={styles.trackPopularity}>{track.popularity ? track.popularity.toLocaleString() : ""}</div>
              <div style={styles.trackDuration}>{duration}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Section({ title, items, type, onPlayTrack, onOpenArtist, onOpenAlbum, onOpenPlaylist }) {
  if (!items?.length) return null;

  return (
    <section style={styles.section}>
      <div style={styles.sectionHead}>
        <h2 style={styles.sectionTitle}>{title}</h2>
      </div>

      <div style={styles.row}>
        {items.slice(0, 12).map((item) => {
          const image =
            type === "track"
              ? item.album?.images?.[0]?.url
              : item.images?.[0]?.url;

          const subtitle =
            type === "track"
              ? item.artists?.map((a) => a.name).join(", ")
              : type === "playlist"
              ? item.owner?.display_name || "Playlist"
              : "Artist";

          return (
            <div
              key={item.id}
              style={styles.card}
              onClick={() =>
                type === "track"
                  ? onPlayTrack?.(item)
                  : type === "artist"
                  ? onOpenArtist?.(item)
                  : type === "album"
                  ? onOpenAlbum?.(item)
                  : type === "playlist"
                  ? onOpenPlaylist?.(item)
                  : null
              }
            >
              <div style={styles.coverWrap}>
                {image ? (
                  <img src={image} alt="" style={styles.cover} />
                ) : (
                  <div style={styles.coverFallback}>♪</div>
                )}
              </div>
              <div style={styles.cardTitle}>{item.name}</div>
              <div style={styles.cardSub}>{subtitle}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const styles = {
  shell: {
    minHeight: "calc(100vh - 260px)",
    paddingBottom: 110,
    display: "grid",
    gridTemplateColumns: "260px minmax(0, 1fr) 320px",
    gap: 18,
    color: "#fff",
    marginTop: 28,
  },
  loginPage: {
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loginPanel: {
    width: 620,
    padding: 42,
    borderRadius: 28,
    background:
      "linear-gradient(145deg, rgba(30,215,96,0.18), rgba(20,20,30,0.96))",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  eyebrow: {
    color: "#1DB954",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  title: {
    fontSize: 54,
    lineHeight: 1,
    margin: "12px 0 18px",
  },
  status: {
    color: "rgba(255,255,255,0.72)",
    fontSize: 18,
    marginBottom: 28,
  },
  button: {
    border: 0,
    borderRadius: 999,
    padding: "15px 26px",
    background: "#1DB954",
    color: "#000",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
  },
  sidebar: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.07)",
    padding: 18,
    minHeight: 620,
  },
  sideTitle: {
    fontWeight: 900,
    fontSize: 18,
    marginBottom: 14,
  },
  sideButton: {
    border: 0,
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    padding: "9px 13px",
    fontWeight: 800,
    marginRight: 8,
    marginBottom: 16,
    cursor: "pointer",
  },

  sideButtonOn: {
    border: 0,
    borderRadius: 999,
    background: "#fff",
    color: "#000",
    padding: "9px 13px",
    fontWeight: 900,
    marginRight: 8,
    marginBottom: 16,
    cursor: "pointer",
  },
  sortRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    margin: "0 0 12px",
  },

  sortLabel: {
    color: "rgba(255,255,255,0.48)",
    fontSize: 12,
    fontWeight: 800,
  },

  sortSelect: {
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 800,
    outline: "none",
  },

  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 30,
  },

  quickTile: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 68,
    borderRadius: 12,
    background: "rgba(255,255,255,0.075)",
    overflow: "hidden",
    cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.06)",
  },

  quickImg: {
    width: 68,
    height: 68,
    objectFit: "cover",
  },

  quickLiked: {
    width: 68,
    height: 68,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, #5c4dff, #1DB954)",
    fontSize: 28,
    fontWeight: 900,
  },

  quickTitle: {
    fontWeight: 950,
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 210,
  },

  quickSub: {
    marginTop: 4,
    color: "rgba(255,255,255,0.52)",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 210,
  },

  likedSongsCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 8px",
    borderRadius: 12,
    marginBottom: 12,
    cursor: "pointer",
    background: "linear-gradient(135deg, rgba(90,70,255,0.35), rgba(29,185,84,0.12))",
    border: "1px solid rgba(255,255,255,0.08)",
  },

  likedIcon: {
    width: 42,
    height: 42,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, #5c4dff, #1DB954)",
    color: "#fff",
    fontWeight: 900,
    fontSize: 20,
  },

  likedHeroIcon: {
    width: 180,
    height: 180,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, #5c4dff, #1DB954)",
    color: "#fff",
    fontWeight: 900,
    fontSize: 82,
    boxShadow: "0 20px 70px rgba(0,0,0,0.45)",
  },

  sideList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: "calc(100vh - 430px)",
    overflowY: "auto",
    paddingRight: 6,
  },
  sideItem: {
    display: "flex",
    cursor: "pointer",
    gap: 10,
    alignItems: "center",
    padding: "7px 8px",
    borderRadius: 10,
    minHeight: 48,
  },
  sideImg: {
    width: 42,
    height: 42,
    objectFit: "cover",
    borderRadius: 6,
    background: "rgba(255,255,255,0.08)",
  },
  sideName: {
    fontSize: 13,
    fontWeight: 800,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 170,
  },
  sideSub: {
    fontSize: 12,
    color: "rgba(255,255,255,0.52)",
  },
  main: {
    minWidth: 0,
    borderRadius: 18,
    background:
      "linear-gradient(180deg, rgba(30,215,96,0.10), rgba(255,255,255,0.025) 260px)",
    border: "1px solid rgba(255,255,255,0.07)",
    padding: 24,
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 26,
  },
  mainTitle: {
    margin: "8px 0 0",
    fontSize: 34,
    letterSpacing: -1,
  },
  disconnect: {
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 999,
    padding: "10px 16px",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },


  backButton: {
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: 999,
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
    marginBottom: 18,
  },

  artistHero: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    padding: 24,
    borderRadius: 20,
    background: "linear-gradient(135deg, rgba(30,215,96,0.18), rgba(255,255,255,0.04))",
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 30,
  },

  artistHeroImg: {
    width: 160,
    height: 160,
    borderRadius: "50%",
    objectFit: "cover",
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  },

  artistName: {
    fontSize: 56,
    lineHeight: 1,
    margin: "10px 0",
    letterSpacing: -2,
  },

  artistMeta: {
    color: "rgba(255,255,255,0.66)",
    fontSize: 14,
    marginBottom: 16,
  },

  spotifyLink: {
    display: "inline-flex",
    color: "#000",
    background: "#1DB954",
    padding: "10px 16px",
    borderRadius: 999,
    fontWeight: 950,
    textDecoration: "none",
  },


  albumHero: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    padding: 24,
    borderRadius: 20,
    background: "linear-gradient(135deg, rgba(229,160,13,0.18), rgba(255,255,255,0.04))",
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 30,
  },

  albumHeroImg: {
    width: 180,
    height: 180,
    borderRadius: 14,
    objectFit: "cover",
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  },

  albumName: {
    fontSize: 48,
    lineHeight: 1,
    margin: "10px 0",
    letterSpacing: -1.5,
  },

  trackList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginTop: 14,
  },

  trackRow: {
    display: "grid",
    gridTemplateColumns: "34px 46px minmax(0,1fr) 80px 48px",
    gap: 12,
    alignItems: "center",
    padding: "9px 10px",
    borderRadius: 10,
    cursor: "pointer",
  },

  trackNum: {
    color: "rgba(255,255,255,0.45)",
    textAlign: "right",
    fontSize: 13,
  },

  trackImg: {
    width: 42,
    height: 42,
    borderRadius: 5,
    objectFit: "cover",
  },

  trackImgFallback: {
    width: 42,
    height: 42,
    borderRadius: 5,
    background: "rgba(255,255,255,0.08)",
    display: "grid",
    placeItems: "center",
  },

  trackInfo: {
    minWidth: 0,
  },

  trackTitle: {
    fontWeight: 900,
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  trackArtist: {
    color: "rgba(255,255,255,0.52)",
    fontSize: 12,
    marginTop: 3,
  },

  trackPopularity: {
    color: "rgba(255,255,255,0.38)",
    fontSize: 12,
    textAlign: "right",
  },

  trackDuration: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 12,
    textAlign: "right",
  },

  searchWrap: {
    display: "flex",
    gap: 12,
    marginBottom: 28,
  },

  searchInput: {
    flex: 1,
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 999,
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    padding: "14px 18px",
    fontSize: 15,
    outline: "none",
  },

  searchButton: {
    border: 0,
    borderRadius: 999,
    background: "#1DB954",
    color: "#000",
    padding: "0 22px",
    fontWeight: 900,
    cursor: "pointer",
  },

  section: {
    marginBottom: 32,
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 22,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(120px, 1fr))",
    gap: 16,
  },
  card: {
    minWidth: 0,
    cursor: "pointer",
    borderRadius: 12,
    background: "rgba(255,255,255,0.055)",
    padding: 12,
  },
  coverWrap: {
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 10,
    background: "rgba(255,255,255,0.08)",
  },
  cover: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  coverFallback: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    fontSize: 40,
    color: "rgba(255,255,255,0.5)",
  },
  cardTitle: {
    fontWeight: 900,
    fontSize: 14,
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardSub: {
    marginTop: 4,
    color: "rgba(255,255,255,0.54)",
    fontSize: 12,
    lineHeight: 1.25,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },


  hifiOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(34px)",
    overflow: "hidden",
  },

  hifiNoise: {
    position: "absolute",
    inset: 0,
    opacity: 0.06,
    backgroundImage:
      "url('https://www.transparenttextures.com/patterns/asfalt-dark.png')",
  },

  hifiClose: {
    position: "absolute",
    top: 26,
    right: 26,
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontSize: 16,
    cursor: "pointer",
    backdropFilter: "blur(14px)",
  },

  hifiCenter: {
    position: "relative",
    zIndex: 2,
    width: "min(92vw, 760px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  hifiArtwork: {
    width: "min(62vw, 420px)",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    borderRadius: 28,
    boxShadow: "0 30px 80px rgba(0,0,0,0.65)",
    marginBottom: 34,
  },

  hifiTrack: {
    fontSize: 40,
    fontWeight: 900,
    letterSpacing: "-0.03em",
    textAlign: "center",
    marginBottom: 10,
    textShadow: "0 4px 18px rgba(0,0,0,0.5)",
  },

  hifiArtist: {
    fontSize: 18,
    opacity: 0.72,
    marginBottom: 34,
    textAlign: "center",
  },

  hifiProgressWrap: {
    position: "relative",
    width: "100%",
    marginBottom: 10,
  },

  hifiProgressTrack: {
    height: 7,
    borderRadius: 999,
    background: "rgba(255,255,255,0.14)",
    overflow: "hidden",
  },

  hifiProgressFill: {
    height: "100%",
    borderRadius: 999,
    background: "#1DB954",
    boxShadow: "0 0 24px rgba(29,185,84,0.85)",
  },

  hifiProgressSlider: {
    position: "absolute",
    inset: 0,
    width: "100%",
    opacity: 0.01,
    cursor: "pointer",
  },

  hifiTimeRow: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    opacity: 0.62,
    fontSize: 12,
    marginBottom: 34,
  },

  hifiControls: {
    display: "flex",
    alignItems: "center",
    gap: 28,
    marginBottom: 34,
  },

  hifiControl: {
    border: 0,
    background: "transparent",
    color: "#fff",
    fontSize: 28,
    cursor: "pointer",
    opacity: 0.82,
  },

  hifiPlay: {
    width: 84,
    height: 84,
    borderRadius: "50%",
    border: 0,
    background: "#fff",
    color: "#000",
    fontSize: 28,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
  },

  hifiVolumeRow: {
    width: "min(420px, 90%)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    opacity: 0.82,
  },

  hifiVolume: {
    flex: 1,
    cursor: "pointer",
  },



  deviceOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100000,
    background: "#020203",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 34,
  },

  deviceClose: {
    position: "fixed",
    top: 22,
    right: 24,
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    cursor: "pointer",
    zIndex: 3,
  },

  deviceScreen: {
    width: "min(94vw, 1360px)",
    aspectRatio: "16 / 9",
    borderRadius: 22,
    background: "linear-gradient(180deg, #101010, #040404)",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 40px 120px rgba(0,0,0,0.75)",
    overflow: "hidden",
    color: "#fff",
  },

  deviceTopBar: {
    height: 74,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 42px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },

  deviceBrand: {
    letterSpacing: 4,
    fontWeight: 900,
    fontSize: 15,
  },

  deviceBadges: {
    display: "flex",
    gap: 18,
    color: "rgba(255,255,255,0.68)",
    fontSize: 15,
  },

  deviceMain: {
    height: "calc(100% - 164px)",
    display: "grid",
    gridTemplateColumns: "38% 39% 23%",
    gap: 0,
    padding: "34px 42px",
  },

  deviceArtWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  deviceArtwork: {
    width: "90%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    borderRadius: 14,
    boxShadow: "0 25px 80px rgba(0,0,0,0.65)",
  },

  deviceArtworkFallback: {
    width: "90%",
    aspectRatio: "1 / 1",
    borderRadius: 14,
    background: "rgba(255,255,255,0.08)",
    display: "grid",
    placeItems: "center",
    fontSize: 80,
  },

  deviceInfo: {
    padding: "34px 42px 0 28px",
  },

  deviceTrack: {
    fontSize: 42,
    fontWeight: 900,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    marginBottom: 14,
  },

  deviceArtist: {
    fontSize: 25,
    color: "rgba(255,255,255,0.72)",
    marginBottom: 8,
  },

  deviceAlbum: {
    fontSize: 22,
    color: "rgba(255,255,255,0.52)",
    marginBottom: 30,
  },

  deviceFormat: {
    display: "flex",
    gap: 12,
    color: "rgba(255,255,255,0.58)",
    fontSize: 15,
    marginBottom: 30,
  },

  devicePcm: {
    fontSize: 31,
    fontWeight: 800,
    marginBottom: 34,
  },

  deviceControls: {
    display: "flex",
    alignItems: "center",
    gap: 32,
    marginBottom: 30,
  },

  deviceControls_button: {},

  deviceControlButton: {
    border: 0,
    background: "transparent",
    color: "#fff",
    fontSize: 34,
    cursor: "pointer",
    opacity: 0.86,
  },

  devicePlay: {
    width: 74,
    height: 74,
    borderRadius: "50%",
    border: "2px solid #d5a85f",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    fontSize: 26,
    cursor: "pointer",
  },

  deviceProgressRow: {
    display: "grid",
    gridTemplateColumns: "52px 1fr 52px",
    alignItems: "center",
    gap: 12,
    color: "rgba(255,255,255,0.78)",
    fontSize: 14,
  },

  deviceProgressWrap: {
    position: "relative",
    height: 14,
  },

  deviceProgressTrack: {
    position: "absolute",
    top: "50%",
    left: 0,
    right: 0,
    transform: "translateY(-50%)",
    height: 6,
    borderRadius: 999,
    background: "rgba(255,255,255,0.16)",
    overflow: "hidden",
  },

  deviceProgressFill: {
    height: "100%",
    background: "#d5a85f",
    boxShadow: "0 0 18px rgba(213,168,95,0.7)",
  },

  deviceProgressSlider: {
    position: "absolute",
    inset: 0,
    width: "100%",
    opacity: 0.01,
    cursor: "pointer",
  },

  deviceSide: {
    borderLeft: "1px solid rgba(255,255,255,0.10)",
    padding: "28px 0 0 36px",
  },

  deviceVolumeLabel: {
    color: "rgba(255,255,255,0.62)",
    fontSize: 15,
    marginBottom: 12,
  },

  deviceDb: {
    fontSize: 44,
    fontWeight: 300,
    marginBottom: 20,
  },

  deviceDb_span: {},

  deviceKnob: {
    width: 174,
    height: 174,
    borderRadius: "50%",
    background: "radial-gradient(circle at 35% 35%, #333, #050505 60%, #222)",
    border: "2px solid rgba(255,255,255,0.45)",
    boxShadow: "inset 0 0 30px rgba(255,255,255,0.08), 0 20px 60px rgba(0,0,0,0.6)",
    position: "relative",
    marginBottom: 22,
  },

  deviceKnobDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#ddd",
    position: "absolute",
    top: 46,
    right: 42,
  },

  deviceVolumeSlider: {
    width: 174,
    cursor: "pointer",
    marginBottom: 30,
  },

  deviceMeters: {
    display: "flex",
    gap: 5,
    alignItems: "flex-end",
    height: 54,
  },

  deviceMeter: {
    width: 6,
    height: 42,
    background: "linear-gradient(to top, #d5a85f, #34d45f)",
    borderRadius: 999,
  },

  deviceNav: {
    height: 90,
    borderTop: "1px solid rgba(255,255,255,0.10)",
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    alignItems: "center",
    textAlign: "center",
    color: "rgba(255,255,255,0.55)",
    background: "rgba(255,255,255,0.025)",
  },

  deviceNavActive: {
    color: "#d5a85f",
  },


  bottomPlayer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    height: 78,
    background: "rgba(8,8,12,0.96)",
    borderTop: "1px solid rgba(255,255,255,0.10)",
    display: "grid",
    gridTemplateColumns: "320px minmax(0,1fr) 260px",
    alignItems: "center",
    padding: "0 20px",
    zIndex: 9999,
    backdropFilter: "blur(18px)",
  },

  bottomLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  bottomImg: {
    width: 52,
    height: 52,
    borderRadius: 6,
    objectFit: "cover",
  },

  bottomImgFallback: {
    width: 52,
    height: 52,
    borderRadius: 6,
    background: "rgba(255,255,255,0.08)",
    display: "grid",
    placeItems: "center",
  },

  bottomTitle: {
    fontWeight: 900,
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 230,
  },

  bottomSub: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 12,
    marginTop: 3,
  },

  bottomCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },

  controls: {
    display: "flex",
    alignItems: "center",
    gap: 18,
  },

  controlButton: {
    border: 0,
    background: "transparent",
    color: "rgba(255,255,255,0.72)",
    fontSize: 16,
    cursor: "pointer",
  },

  playCircle: {
    width: 34,
    height: 34,
    border: 0,
    borderRadius: "50%",
    background: "#fff",
    color: "#000",
    display: "grid",
    placeItems: "center",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },

  progressRow: {
    display: "grid",
    gridTemplateColumns: "44px minmax(240px, 620px) 44px",
    gap: 10,
    alignItems: "center",
    color: "rgba(255,255,255,0.55)",
    fontSize: 11,
  },


  progressWrap: {
    position: "relative",
    width: "100%",
    height: 14,
    display: "flex",
    alignItems: "center",
  },

  progressSlider: {
    position: "absolute",
    inset: 0,
    width: "100%",
    opacity: 0.01,
    cursor: "pointer",
    zIndex: 2,
  },

  volumeWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
  },

  volumeIcon: {
    fontSize: 14,
    opacity: 0.72,
  },

  volumeSlider: {
    width: 110,
    cursor: "pointer",
  },


  progressTrack: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    height: 5,
    borderRadius: 999,
    background: "rgba(255,255,255,0.24)",
    overflow: "hidden",
    pointerEvents: "none",
  },

  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: "#1DB954",
    boxShadow: "0 0 10px rgba(29,185,84,0.75)",
  },

  bottomRight: {
    textAlign: "right",
    color: "rgba(255,255,255,0.45)",
    fontSize: 12,
  },

  nowPlaying: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.07)",
    padding: 18,
    minHeight: 620,
  },
  panelTitle: {
    fontSize: 18,
    fontWeight: 900,
    marginBottom: 16,
  },
  nowImg: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    borderRadius: 14,
    marginBottom: 16,
  },
  nowImgFallback: {
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: 14,
    marginBottom: 16,
    background: "rgba(255,255,255,0.08)",
    display: "grid",
    placeItems: "center",
    fontSize: 48,
    color: "rgba(255,255,255,0.45)",
  },

  nowTitle: {
    fontSize: 22,
    fontWeight: 950,
    lineHeight: 1.1,
  },
  nowSub: {
    color: "rgba(255,255,255,0.6)",
    marginTop: 8,
  },
  empty: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 14,
    lineHeight: 1.4,
  },
};

function QuickShelf({ playlists, albums, likedSongs, onLiked, onPlaylist, onAlbum }) {
  const firstPlaylists = (playlists || []).slice(0, 3);
  const firstAlbums = (albums || []).map((x) => x.album || x).filter(Boolean).slice(0, 2);

  return (
    <div style={styles.quickGrid}>
      <div style={styles.quickTile} onClick={onLiked}>
        <div style={styles.quickLiked}>♥</div>
        <div>
          <div style={styles.quickTitle}>Liked Songs</div>
          <div style={styles.quickSub}>{likedSongs?.length || 0} saved tracks</div>
        </div>
      </div>

      {firstPlaylists.map((p) => (
        <div key={p.id} style={styles.quickTile} onClick={() => onPlaylist(p)}>
          <img src={p.images?.[0]?.url} alt="" style={styles.quickImg} />
          <div>
            <div style={styles.quickTitle}>{p.name}</div>
            <div style={styles.quickSub}>Playlist</div>
          </div>
        </div>
      ))}

      {firstAlbums.map((a) => (
        <div key={a.id} style={styles.quickTile} onClick={() => onAlbum(a)}>
          <img src={a.images?.[0]?.url} alt="" style={styles.quickImg} />
          <div>
            <div style={styles.quickTitle}>{a.name}</div>
            <div style={styles.quickSub}>{a.artists?.map((x) => x.name).join(", ")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LikedSongsDetail({ likedSongs, onBack, onPlayTrack }) {
  const tracks = (likedSongs || [])
    .map((x) => ({
      ...(x.track || {}),
      added_at: x.added_at,
    }))
    .filter((x) => x?.uri);

  return (
    <div>
      <button style={styles.backButton} onClick={onBack}>
        ← Back to Spotify
      </button>

      <div style={styles.albumHero}>
        <div style={styles.likedHeroIcon}>♥</div>

        <div>
          <div style={styles.eyebrow}>Playlist</div>

          <h1 style={styles.albumName}>Liked Songs</h1>

          <div style={styles.artistMeta}>
            {tracks.length} saved tracks from your Spotify library
          </div>
        </div>
      </div>

      <TrackList
        title="Tracks"
        tracks={tracks}
        onPlayTrack={onPlayTrack}
      />
    </div>
  );
}

function PlaylistDetail({ playlist, playlistTracks, deviceId, onBack, onPlayTrack }) {
  const image = playlist.images?.[0]?.url;

  return (
    <div>
      <button style={styles.backButton} onClick={onBack}>
        ← Back to Spotify
      </button>

      <div style={styles.albumHero}>
        {image && (
          <img
            src={image}
            alt=""
            style={styles.albumHeroImg}
          />
        )}

        <div>
          <div style={styles.eyebrow}>Playlist</div>

          <h1 style={styles.albumName}>
            {playlist.name}
          </h1>

          <div style={styles.artistMeta}>
            {playlist.owner?.display_name}
            {playlist.tracks?.total
              ? ` · ${playlist.tracks.total} tracks`
              : ""}
          </div>
        </div>
      </div>

      <button
        style={styles.button}
        onClick={() => playContextOnly(playlist.uri, deviceId)}
      >
        Play Playlist
      </button>

      <TrackList
        title="Tracks"
        tracks={(playlistTracks || [])
  .map((x) => ({
    ...(x.track || {}),
    album: x.track?.album || playlist,
    context_uri: playlist.uri,
  }))
  .filter((x) => x?.uri)}
        onPlayTrack={onPlayTrack}
      />
    </div>
  );
}
